import type { Label } from '@/entities/board';
import { formatCalendarDate } from '@/shared/lib/datetime';

import styles from './CompactWeek.module.css';
import { barStyle, itemFill, labelColorsOf } from './labels';
import { type CalendarEvent, type DayLoad, eventsOnDay, timeRangeOnDay } from './model';
import { StateTag } from './StateTag';
import { Workload } from './Workload';

interface Props {
  /** The days to show, in order: seven for Week, five for Work week. */
  days: readonly string[];
  events: readonly CalendarEvent[];
  today: string;
  locale: string;
  colorOf: (boardId: string) => string;
  /** "Board · Workspace" of an item's source, or '' when every item comes from one board. */
  sourceOf: (boardId: string) => string;
  labels: readonly Label[];
  /** Daily workload in each day's header (null = off). */
  workload?: ReadonlyMap<string, DayLoad> | null;
  onOpenCard: (cardId: string) => void;
}

/** The interval of an event on `day` (start and end); a timed deadline keeps its due time. */
function timeOf(event: CalendarEvent, day: string): string {
  if (event.allDay) return '';
  return timeRangeOnDay(event, day) ?? event.start.slice(11, 16);
}

/**
 * Week / Work week for a desktop without an hour grid: one column per day with short task rows
 * (time, title, source). EVERY task of a day is listed: a day with more tasks than the height
 * allows scrolls inside its own column (the day headers stay in view). Reading and navigating only: moving or resizing is the hour grid's job.
 * Work week shows exactly its five days; weekend tasks stay one navigation or one switch away,
 * they are never dropped from the data.
 */
export function CompactWeek({
  days,
  events,
  today,
  locale,
  colorOf,
  sourceOf,
  labels,
  workload = null,
  onOpenCard,
}: Props) {
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
  return (
    <div
      className={styles.week}
      style={{ gridTemplateColumns: `repeat(${String(days.length)}, minmax(0, 1fr))` }}
      data-testid="compact-week"
      data-days={days.length}
    >
      {days.map((day) => {
        const items = eventsOnDay(events, day);
        const date = new Date(`${day}T00:00:00Z`);
        return (
          <section
            key={day}
            className={`${styles.day} ${day === today ? styles.today : ''}`}
            aria-label={`${weekday.format(date)}, ${formatCalendarDate(day, locale)}`}
            data-testid="compact-day"
            data-day={day}
          >
            <header className={styles.head}>
              <span className={styles.weekday}>{weekday.format(date)}</span>
              <span className={styles.number} aria-current={day === today ? 'date' : undefined}>
                {Number(day.slice(8, 10))}
              </span>
              {items.length > 0 && <span className={styles.count}>{items.length}</span>}
              <Workload load={workload?.get(day)} />
            </header>
            {items.length === 0 ? (
              <p className={styles.empty}>—</p>
            ) : (
              <ul className={styles.list}>
                {items.map((event) => {
                  const done = event.card.completed_at !== null;
                  const source = sourceOf(event.boardId);
                  return (
                    <li key={event.id}>
                      <button
                        type="button"
                        className={`${styles.item} ${done ? styles.done : ''} ${
                          event.card.is_overdue ? styles.overdue : ''
                        } ${event.phase === 'past' && !done ? styles.past : ''}`}
                        data-testid="compact-item"
                        title={[event.card.title, source].filter(Boolean).join(' — ')}
                        onClick={() => {
                          onOpenCard(event.id);
                        }}
                      >
                        <span
                          className={styles.bar}
                          style={barStyle(
                            itemFill(labelColorsOf(event.card, labels)),
                            colorOf(event.boardId),
                          )}
                          aria-hidden
                        />
                        <span className={styles.text}>
                          <span className={styles.title}>
                            {timeOf(event, day) && (
                              <span className={styles.time}>{timeOf(event, day)}</span>
                            )}
                            <span className={styles.titleText}>{event.card.title}</span>
                          </span>
                          {source && <span className={styles.source}>{source}</span>}
                          <StateTag event={event} />
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
